import { logger } from './logger';

const RECOVERY_ATTEMPT_KEY = 'app-deploy-recovery-attempt';
const RECOVERY_QUERY_PARAM = '__app_update';
const RECOVERY_RESET_DELAY_MS = 15000;

const RECOVERABLE_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
  /Unable to preload CSS/i,
  /dynamically imported module/i,
  /module script/i,
];

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (error) {
    logger.warn('Session storage is unavailable for deploy recovery:', error);
    return null;
  }
};

const extractErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
  }

  return '';
};

const hasRecoveryMarkerInUrl = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const currentUrl = new URL(window.location.href);
    return currentUrl.searchParams.has(RECOVERY_QUERY_PARAM);
  } catch {
    return false;
  }
};

const hasRecentRecoveryAttempt = () => {
  const storage = getSessionStorage();

  if (storage) {
    try {
      return Boolean(storage.getItem(RECOVERY_ATTEMPT_KEY));
    } catch (error) {
      logger.warn('Failed to read deploy recovery attempt state:', error);
    }
  }

  return hasRecoveryMarkerInUrl();
};

const setRecoveryAttempt = (source: string, error: unknown) => {
  const storage = getSessionStorage();

  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      RECOVERY_ATTEMPT_KEY,
      JSON.stringify({
        source,
        message: extractErrorMessage(error),
        at: Date.now(),
      }),
    );
  } catch (storageError) {
    logger.warn('Failed to persist deploy recovery state:', storageError);
  }
};

export const isRecoverableDeployError = (error: unknown) => {
  const message = extractErrorMessage(error);

  if (!message) {
    return false;
  }

  return RECOVERABLE_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

export const beginDeployRecovery = (error: unknown, source: string) => {
  if (typeof window === 'undefined' || !isRecoverableDeployError(error)) {
    return false;
  }

  if (hasRecentRecoveryAttempt()) {
    logger.warn('Deploy recovery already attempted for this session window');
    return false;
  }

  setRecoveryAttempt(source, error);

  try {
    const recoveryUrl = new URL(window.location.href);
    recoveryUrl.searchParams.set(RECOVERY_QUERY_PARAM, Date.now().toString());

    logger.warn('Recovering from deploy/update asset mismatch', {
      source,
      message: extractErrorMessage(error),
    });

    window.location.replace(recoveryUrl.toString());
    return true;
  } catch (reloadError) {
    logger.error('Failed to build deploy recovery URL:', reloadError);
    window.location.reload();
    return true;
  }
};

export const finalizeDeployRecoveryBootstrap = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const currentUrl = new URL(window.location.href);

    if (currentUrl.searchParams.has(RECOVERY_QUERY_PARAM)) {
      currentUrl.searchParams.delete(RECOVERY_QUERY_PARAM);
      window.history.replaceState({}, document.title, currentUrl.toString());
    }
  } catch (error) {
    logger.warn('Failed to clean deploy recovery URL state:', error);
  }

  window.setTimeout(() => {
    const storage = getSessionStorage();

    if (!storage) {
      return;
    }

    try {
      storage.removeItem(RECOVERY_ATTEMPT_KEY);
    } catch (error) {
      logger.warn('Failed to clear deploy recovery state:', error);
    }
  }, RECOVERY_RESET_DELAY_MS);
};

/**
 * Clears all deploy/chunk recovery state from sessionStorage immediately.
 * Call this before a manual hard reload so auto-recovery can restart cleanly.
 */
export const clearDeployRecoveryState = () => {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(RECOVERY_ATTEMPT_KEY);
    // Also clear the lazyWithReload key used in App.tsx
    storage.removeItem('chunk-reload');
  } catch (error) {
    logger.warn('Failed to clear deploy recovery state:', error);
  }
};
